// @ts-expect-error
import html2pdf from 'html2pdf.js';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  beginHtml2PdfExport,
  endHtml2PdfExport,
} from '@/utils/html2pdf-overlay';
import { AccoutingDoctorAttendingViewModel } from '../use-accounting-doctor-attending.viewmodel';

export const useExportAccountingDoctorAttendingToPdfViewModel = ({
  information,
}: {
  information: AccoutingDoctorAttendingViewModel['information'];
}) => {
  const contentRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const [exporting, setExporting] = useState(false);

  // Fonction pour exporter en PDF
  const exportToPdf = async (): Promise<Blob | null> => {
    if (!information) {
      return null;
    }

    setExporting(true);

    const filename = `${t('accounting-doctor-attending.export-filename', {
      office: information.officeName,
      doctorAttending: information.doctorAttending.lastname.toLowerCase(),
      from: String(information.period.from),
      to: String(information.period.to),
    })}.pdf`;

    // Configuration de html2pdf
    const options = {
      margin: 10,
      filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 4, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    };

    beginHtml2PdfExport();
    try {
      const blob: Blob = await html2pdf()
        .from(contentRef.current)
        .set(options)
        .outputPdf('blob');

      // Trigger browser download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return blob;
    } finally {
      // A rendering failure rejects out of this function so the caller reports
      // it instead of uploading nothing under a success toast.
      endHtml2PdfExport();
      setExporting(false);
    }
  };

  return {
    contentRef,
    exporting,
    export: exportToPdf,
  };
};
export type ExportAccountingDoctorAttendingToPdfViewModel = ReturnType<
  typeof useExportAccountingDoctorAttendingToPdfViewModel
>;
